/**
 * @aris/request-watcher-react — public API.
 *
 *   import { initArisRequestWatcher } from '@aris/request-watcher-react';
 *   initArisRequestWatcher({ projectId, environment, collectorUrl, apiKey, showWidget: true });
 */
import { resolveConfig, type ArisConfig } from './config.js';
import { Runtime, setRuntime, getRuntime } from './capture.js';
import { installFetch, restoreFetch } from './interceptors/fetch.js';
import { installXhr, restoreXhr } from './interceptors/xhr.js';
import { installTraceTriggers } from './trace.js';
import { mountWidget } from './widget.js';
import { installErrorCapture } from './errors.js';
import { installWebVitals } from './vitals.js';
import { installLongTasks } from './longtasks.js';
import { installMemorySampling } from './memory.js';
import { installProfiler } from './profiler.js';
import { installSignals, type Signals } from './signals.js';
import { store } from './store.js';

export type { ArisConfig } from './config.js';
export type { MetricType } from './signals.js';
export { attachAxios } from './interceptors/axios.js';
export { startTrace } from './trace.js';
export { captureException } from './errors.js';

export interface ArisHandle {
  /** Force-send buffered events now (e.g. before a hard navigation). */
  flush: () => Promise<void>;
  /** Restore all patched globals and remove the widget. */
  shutdown: () => void;
  /** Live counters for building a custom UI. */
  getCounters: () => typeof store.counters;
  /** Record a custom metric. */
  metric: Signals['metric'];
  /** Emit a structured log line. */
  log: Signals['log'];
}

let started = false;
let signals: Signals | null = null;

/** Record a custom metric: aris.metric('checkout.total', 42, { type: 'counter' }). */
export function metric(...args: Parameters<Signals['metric']>): void {
  signals?.metric(...args);
}
/** Emit a structured log: aris.log('error', 'payment failed', { orderId }). */
export function log(...args: Parameters<Signals['log']>): void {
  signals?.log(...args);
}

export function initArisRequestWatcher(config: ArisConfig): ArisHandle {
  if (started && getRuntime()) {
    if (config.debug) console.warn('[aris] already initialized; ignoring duplicate init');
    return makeHandle(() => {});
  }
  if (typeof window === 'undefined') {
    // SSR guard: do nothing on the server; the backend SDK handles server traffic.
    return makeHandle(() => {});
  }

  const cfg = resolveConfig(config);
  const rt = new Runtime(cfg);
  setRuntime(rt);
  rt.transport.start();

  const teardown: Array<() => void> = [];
  teardown.push(installTraceTriggers());
  installFetch();
  installXhr();
  teardown.push(restoreFetch, restoreXhr);

  if (cfg.captureErrors) teardown.push(installErrorCapture(cfg));
  if (cfg.captureWebVitals) teardown.push(installWebVitals(cfg));
  teardown.push(installLongTasks(cfg));
  teardown.push(installMemorySampling(cfg));
  teardown.push(installProfiler(cfg));
  signals = installSignals(cfg);
  teardown.push(() => { signals?.stop(); signals = null; });
  if (cfg.showWidget) teardown.push(mountWidget(cfg));

  started = true;
  if (cfg.debug) console.info('[aris] request watcher started', { project: cfg.projectId, env: cfg.environment });

  return makeHandle(() => {
    rt.transport.stop();
    for (const t of teardown) t();
    setRuntime(null as unknown as Runtime);
    started = false;
  });
}

function makeHandle(shutdown: () => void): ArisHandle {
  return {
    flush: async () => {
      await getRuntime()?.transport.flush();
    },
    shutdown,
    getCounters: () => store.counters,
    metric: (...args) => signals?.metric(...args),
    log: (...args) => signals?.log(...args),
  };
}
